"use client"

import { useLocale, useTranslations } from "next-intl"
import { Checkbox } from "@/components/ui/checkbox"
import { Calendar, PackageCheck } from "lucide-react"

export interface PreorderItemSummary {
  listingId: string
  title: string
  estimated_delivery?: string | null
  min_orders?: number | null
  preorder_status?: "open" | "funded" | "in_production" | "shipped" | null
}

function formatDate(value: string | null | undefined, locale: string): string | null {
  if (!value) return null
  try {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return null
    return d.toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" })
  } catch {
    return null
  }
}

/**
 * Amber consent block for preorder line items.
 *
 * Colour is a `light dark:dark` pair, not a `darkUi` prop: the sole caller
 * (`checkout-form`) always renders inside an overlay panel, so the former
 * forced-dark branch and the `dark:` half are the same paint today — and the
 * pair keeps paining correctly once the panel follows the theme (Phase 2 step
 * 2f). @see .tondocs/ui/HANDOFF_SHEET_LIGHT_THEME_2026_08_01.md §7
 */
export function PreorderCheckoutBlock({
  items,
  consented,
  onConsentChange,
}: {
  items: PreorderItemSummary[]
  consented: boolean
  onConsentChange: (next: boolean) => void
}) {
  const t = useTranslations("preorder.checkout")
  const locale = useLocale()
  if (items.length === 0) return null

  return (
    <div
      className="rounded-2xl border p-4 space-y-3 border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10"
      role="group"
      aria-labelledby="preorder-block-title"
    >
      <div className="flex items-start gap-3">
        <PackageCheck className="mt-0.5 h-5 w-5 text-amber-600 dark:text-amber-300" />
        <div className="space-y-2">
          <p
            id="preorder-block-title"
            className="font-semibold text-sm text-amber-900 dark:text-amber-100"
          >
            {items.length === 1 ? t("singleTitle") : t("multipleTitle")}
          </p>
          <ul className="space-y-2 text-sm">
            {items.map((item) => {
              const eta = formatDate(item.estimated_delivery, locale)
              return (
                <li key={item.listingId} className="text-amber-900/90 dark:text-amber-100/90">
                  <span className="font-medium">{item.title}</span>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-amber-800/80 dark:text-amber-200/80">
                    {eta && (
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        {t("eta", { date: eta })}
                      </span>
                    )}
                    {item.min_orders != null && item.preorder_status === "open" && (
                      <span>{t("productionThreshold", { min: item.min_orders })}</span>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
          <p className="text-xs text-amber-900/80 dark:text-amber-200/80">
            {t("paymentCaptured")}
          </p>
        </div>
      </div>

      <label className="flex items-start gap-2 cursor-pointer select-none">
        <Checkbox
          checked={consented}
          onCheckedChange={(v) => onConsentChange(Boolean(v))}
          aria-required
          aria-label={t("consent")}
          className="mt-0.5"
        />
        <span className="text-sm text-amber-900 dark:text-amber-100">
          {t("consent")}
        </span>
      </label>
    </div>
  )
}
